import { Ref, useCallback, useEffect, useState } from "react";
import { Key } from "react-aria-components";
import { ISearchDropdownOptions, SearchDropdown } from "ui-components";
import { SearchDropdownAlt } from "ui-components/src/components/SearchDropdown/SearchDropdownAlt";
import { useGoogleMapsScript, Libraries } from "use-google-maps-script";
import usePlacesAutocomplete from "use-places-autocomplete";

interface ILocation {
  description: string;
}
interface ISearchLocationProps {
  onSelectAddress: (address: string) => void;
  defaultValue?: string | null;
  placeholder?: string;
  errorMessage?: string;
  helperMessage?: string;
  label?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "full" | null | undefined;
  inputValue?: string;
  isCountryAllowed?: boolean;
  hasValue?: boolean;
  isRequired?: boolean;
  inputReference?: Ref<HTMLInputElement> | undefined;
  addSearchKeywordAsOption?: boolean;
}

const libraries: Libraries = ["places"];

export function SearchLocation({
  onSelectAddress,
  defaultValue,
  placeholder = "Location",
  label = "Location",
  isRequired,
  size = "md",
  isCountryAllowed = true,
  inputValue,
  errorMessage,
  helperMessage,
  hasValue,
  inputReference,
  addSearchKeywordAsOption,
}: ISearchLocationProps): JSX.Element {
  const { isLoaded, loadError } = useGoogleMapsScript({
    googleMapsApiKey: process.env.GOOGLE_PLACES_API_KEY ?? "",
    libraries,
  });

  if (!isLoaded) {
    return (
      <SearchDropdown
        label={label}
        isRequired={isRequired}
        placeholder={placeholder}
        size={size}
        isDisabled
      />
    );
  }
  if (loadError) {
    return <div>Error loading</div>;
  }

  return (
    <ReadySearchBox
      label={label}
      isRequired={isRequired}
      placeholder={placeholder}
      onSelectAddress={onSelectAddress}
      defaultValue={defaultValue}
      size={size}
      inputValue={inputValue}
      isCountryAllowed={isCountryAllowed}
      errorMessage={errorMessage}
      helperMessage={helperMessage}
      hasValue={hasValue}
      inputReference={inputReference}
      addSearchKeywordAsOption={addSearchKeywordAsOption}
    />
  );
}

function ReadySearchBox({
  label,
  placeholder,
  inputValue,
  onSelectAddress,
  defaultValue,
  isRequired,
  size,
  errorMessage,
  helperMessage,
  hasValue,
  isCountryAllowed = true,
  inputReference,
  addSearchKeywordAsOption,
}: ISearchLocationProps): JSX.Element {
  const [selectedKey, setSelectedKey] = useState<Key>();
  const [inputText, setInputText] = useState("");

  useEffect(() => {
    if (!defaultValue) {
      setInputText("");
      setSelectedKey("");
    } else {
      setInputText(defaultValue);
      setSelectedKey(defaultValue);
    }
  }, [defaultValue]);

  const {
    ready,
    setValue,
    suggestions: { data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 50,
    defaultValue: defaultValue ?? "",
    requestOptions: {
      types: ["(regions)"],
    },
  });

  const handleChange = (newInputValue: string): void => {
    setInputText(newInputValue);
    setValue(newInputValue);
    if (newInputValue === "" || newInputValue === undefined) {
      onSelectAddress("");
    }
  };

  const handleSelect = (address: string): void => {
    if (address) {
      setValue(address, false);
      clearSuggestions();
      onSelectAddress(address);
    }
  };

  const getOptions = useCallback(
    (locations: ILocation[], text: string): ISearchDropdownOptions[] => {
      const filteredLocations: ISearchDropdownOptions[] =
        locations?.reduce((acc, { description }) => {
          if (description) {
            if (
              !isCountryAllowed &&
              (description.includes(",") || description.includes("-"))
            ) {
              acc.push({ id: description, name: description });
            } else if (isCountryAllowed) {
              acc.push({ id: description, name: description });
            }
          }
          return acc;
        }, [] as ISearchDropdownOptions[]) || [];

      if (!addSearchKeywordAsOption) {
        return filteredLocations;
      }

      if (text && !filteredLocations.some((location) => location.id === text)) {
        return [{ id: text, name: text }, ...filteredLocations];
      }

      return filteredLocations;
    },
    [isCountryAllowed, addSearchKeywordAsOption]
  );

  return (
    <SearchDropdownAlt
      id={label}
      isDisabled={!ready}
      label={label}
      isRequired={isRequired}
      size={size}
      inputText={inputText}
      inputValue={inputValue}
      defaultInputValue={defaultValue ?? ""}
      defaultValue={defaultValue ?? ""}
      placeholder={placeholder}
      onInputChange={handleChange}
      errorMessage={errorMessage}
      helperMessage={helperMessage}
      hasValue={hasValue}
      isLocation
      selectedKey={selectedKey}
      onSelectionChange={(selectedCity) => {
        setSelectedKey(selectedCity);
        handleSelect(selectedCity?.toString());
      }}
      inputReference={inputReference}
      options={getOptions(data, inputText)}
    />
  );
}
